import React from "react";
import PropTypes from "prop-types";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import CircularProgress from "@material-ui/core/CircularProgress";
import useSimpleComparator from "../utils/useSimpleComparator";

/**
 *
 * Komponente, welche ein einfaches auswählen von Elementen ermöglicht.
 *
 * @param label Beschriftung der Komponente -> dient gleichzeitig als id
 * @param options die auswählbaren Optionen
 * @param optionTitle Die Property, welche aus den options als Wert angezeigt werden soll. Bei Anzeige von Strings anstatt von Objekten, muss der optionTitle leer bleiben
 * @param optionKey Wenn der übergebene optionTitle nicht unique ist, muss ein optionKey übergeben werden, welcher die übergebenen options auseinanderhalten kann
 * @param loading boolean, welcher anzeigt, dass die auszuwählenden Elemente noch aus dem Backend geladen werden
 * @param ...rest Weitere Attribute, welche an das Select-Element weitergeleitet werden. Beispielsweise sollten hier <strong>value und onChange</strong> enthalten sein
 *  @returns {JSX.Element}
 * @constructor
 */
function EASelect({ label, options, optionTitle, optionKey, value, onChange, error, required, loading, ...rest }) {
    const labelId = `${label}-label`;

    const comparator = useSimpleComparator(optionTitle);
    const sortedOptions = options ? options.sort(comparator) : options;

    function buildInfoOption() {
        if (sortedOptions && sortedOptions.length > 0) {
            return (
                !required && (
                    <MenuItem key={null} value={null} disabled={!value}>
                        {label + " abwählen"}
                    </MenuItem>
                )
            );
        } else {
            return (
                <MenuItem disabled key={null} value={null}>
                    {(loading && <CircularProgress />) || "Keine Einträge vorhanden"}
                </MenuItem>
            );
        }
    }

    return (
        <FormControl error={error} variant="outlined" style={rest.style} className={rest.className}>
            <InputLabel error={error} id={labelId} required={required}>
                {label}
            </InputLabel>
            <Select
                labelId={labelId}
                id={label}
                label={label}
                value={getValue(value, sortedOptions, optionKey, optionTitle)}
                onChange={(e) => onChange(findOption(e.target.value, sortedOptions, optionKey, optionTitle))}
                required={required}
                {...rest}
            >
                {buildInfoOption()}
                {sortedOptions && sortedOptions.map((option) => createOption(option, optionTitle, optionKey))}
            </Select>
            {error && (
                <FormHelperText id={label + "-helper-text"} error>
                    {error}
                </FormHelperText>
            )}
        </FormControl>
    );
}

function getValue(value, options, optionKey, optionTitle) {
    if (!value || !options || options.length === 0) {
        return "";
    }
    const newVal = getOptionKey(value, getOptionTitle(value, optionTitle), optionKey);
    if (newVal) {
        return newVal;
    }
    return "";
}

function findOption(value, options, optionKey, optionTitle) {
    if (!value || !options) {
        return undefined;
    }
    return options.find((o) => getOptionKey(o, getOptionTitle(o, optionTitle), optionKey) === value);
}

function getOptionTitle(option, optionTitleProperty) {
    return optionTitleProperty ? option[optionTitleProperty] : option;
}

function getOptionKey(option, optionTitle, optionKeyProperty) {
    return optionKeyProperty ? option[optionKeyProperty] : optionTitle;
}

function createOption(option, optionTitleProperty, optionKeyProperty) {
    const optionTitle = getOptionTitle(option, optionTitleProperty);
    const optionKey = getOptionKey(option, optionTitle, optionKeyProperty);
    return (
        <MenuItem key={optionKey} value={optionKey}>
            {optionTitle}
        </MenuItem>
    );
}

EASelect.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.array,
    optionTitle: PropTypes.string,
    optionKey: PropTypes.string,
    loading: PropTypes.bool,
};

export default EASelect;
